// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';

// bernie
import { ContextStore, RouteStore } from 'bernie-core';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { UserStore } from 'stores';

// util
import { getFormSubmitValues } from '../../util/GetFormSubmitValues';
import { Validation } from '../../util/Validation';
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';

// ui
import { UitkEmailInput, UitkTextInput } from 'uitk-react-input';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLink } from 'uitk-react-link';
import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkSelect } from 'uitk-react-select';
import { UitkSpacing } from 'uitk-react-spacing';
import { EmailDialog } from './email-dialog';

interface SignUpFormProps {
  context: ContextStore;
  industries: string[];
  l10n?: ILocalization;
  routeStore: RouteStore;
  user?: UserStore;
  locale?: string;
}

export const SignUpForm: React.FunctionComponent<SignUpFormProps> = ({
  context,
  industries,
  l10n,
  routeStore,
  user,
  locale,
}) => {
  const [formError, setFormError] = React.useState(null);
  const [verificationEmailError, setVerificationEmailError] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [email, setEmail] = React.useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);

  const { control, errors, handleSubmit } = useForm({ mode: 'onBlur' });

  // labels
  const emailLabel = l10n.formatText('email');
  const firstNameLabel = l10n.formatText('sign.up.first.name');
  const lastNameLabel = l10n.formatText('sign.up.last.name');
  const submitLabel = l10n.formatText('submit');
  const websiteLabel = l10n.formatText('sign.up.website');
  const companyLabel = l10n.formatText('sign.up.company');
  const industryLabel = l10n.formatText('sign.up.industry');
  const countryLabel = l10n.formatText('sign.up.country');

  // these need adding to localization repo
  // missing labels
  const emailMissingErrorLabel = l10n.formatText('sign.up.email.missing.label');
  const firstNameMissingErrorLabel = l10n.formatText('sign.up.firstName.missing.label');
  const lastNameMissingErrorLabel = l10n.formatText('sign.up.lastName.missing.label');
  const websiteMissingErrorLabel = l10n.formatText('sign.up.website.missing.label');
  const companyMissingErrorLabel = l10n.formatText('sign.up.company.missing.label');
  const industryMissingErrorLabel = l10n.formatText('sign.up.industry.missing.label');
  const countryMissingErrorLabel = l10n.formatText('sign.up.country.missing.label');

  // invalid labels
  const emailInvalidErrorLabel = l10n.formatText('sign.up.email.invalid.label');

  const sendVerificationEmail = async (verificationEmail) => {
    await user.sendVerificationEmail(verificationEmail, modifyLocaleParam(context.locale)).catch(() => {
      setVerificationEmailError('Email communication failed. Please try again');
    });
  };

  const scrollWindow = (xpos, ypos) => {
    window.scrollTo(xpos, ypos);
  };

  const onSubmit = handleSubmit((fields) => {
    setStatus('LOADING');
    setEmail(fields.email);

    triggerSignup(fields);
  });

  const triggerSignup = async (fields) => {
    await user.createUser(fields).then((response) => {
      setStatus(null);
      setFormError(null);

      if (response.success) {
        setEmailDialogOpen(true);
        sendVerificationEmail(fields.email);

        return;
      } else {
        if (response.resBody) {
          if (response.resBody.message === 'USER_ACCOUNT_EXISTS_ERROR') {
            setFormError(l10n.formatText('sign.up.existing.email.error.message'));
          } else {
            setFormError(response.error);
          }
        } else {
          setFormError(response.error);
        }
        scrollWindow(0, 0);
      }
    });
  };

  return (
    <>
      {emailDialogOpen && (
        <EmailDialog
          email={email}
          l10n={l10n}
          routeStore={routeStore}
          sendVerificationEmail={sendVerificationEmail}
          verificationEmailError={verificationEmailError}
          locale={locale}
        />
      )}
      <form name={'signUpForm'} onSubmit={onSubmit} method="POST">
        {formError && (
          <UitkSpacing margin={{ blockend: 'six' }}>
            <div>
              <UitkErrorSummary heading={formError} />
            </div>
          </UitkSpacing>
        )}
        <UitkLayoutFlex direction={'column'} space="three">
          <UitkLayoutFlexItem>
            <UitkHeading tag="h2" size={5}>
              <LocalizedText message="sign.up.about.yourself" />
            </UitkHeading>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="email"
              defaultValue=""
              rules={{
                required: emailMissingErrorLabel,
                pattern: {
                  value: Validation.EMAIL_REGEX,
                  message: emailInvalidErrorLabel,
                },
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkEmailInput
                  id="signUpFormEmailInput"
                  name={name}
                  label={emailLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.email?.message}
                  data-cy="sign-up-email-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="firstName"
              defaultValue=""
              rules={{
                required: firstNameMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormFirstNameInput"
                  name={name}
                  label={firstNameLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.firstName?.message}
                  data-cy="sign-up-first-name-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="lastName"
              defaultValue=""
              rules={{
                required: lastNameMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormLastNameInput"
                  name={name}
                  label={lastNameLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.lastName?.message}
                  data-cy="sign-up-last-name-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing padding={{ blockstart: 'six' }}>
              <UitkHeading tag="h2" size={5}>
                <LocalizedText message="sign.up.about.business" />
              </UitkHeading>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="website"
              defaultValue=""
              rules={{
                required: websiteMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormWebsiteInput"
                  name={name}
                  label={websiteLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.website?.message}
                  data-cy="sign-up-website-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="company"
              defaultValue=""
              rules={{
                required: companyMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormCompanyInput"
                  name={name}
                  label={companyLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.company?.message}
                  data-cy="sign-up-company-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="industry"
              defaultValue=""
              rules={{
                required: industryMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkSelect
                  id="signUpFormIndustryInput"
                  name={name}
                  label={industryLabel}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.industry?.message}
                  emptyState={true}
                  data-cy="sign-up-industry-field"
                >
                  {industries &&
                    industries.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                </UitkSelect>
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="country"
              defaultValue=""
              rules={{
                required: countryMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormCountryInput"
                  name={name}
                  label={countryLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.country?.message}
                  data-cy="sign-up-country-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing padding={{ block: 'six' }}>
              <UitkText>
                <LocalizedText message="sign.up.disclaimer.prefix" />{' '}
                <UitkLink inline={true}>
                  <Link to={`/${modifyLocaleParam(locale)}/terms-of-use`} target="_blank">
                    <LocalizedText message="sign.up.termsofuse" />
                  </Link>
                </UitkLink>{' '}
                <LocalizedText message="sign.up.disclaimer.suffix" />{' '}
                <UitkLink inline={true}>
                  <Link to={`/${modifyLocaleParam(locale)}/privacy-policy`} target="_blank">
                    <LocalizedText message="sign.up.privacy.policy" />
                  </Link>
                </UitkLink>
              </UitkText>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkPrimaryButton
              data-cy="sign-up-submit"
              id="signUpFormSubmitButton"
              type="submit"
              className={'spacing-top-bottom-24'}
              disabled={errors.length > 0 || status === 'LOADING'}
              size={UitkButtonSize.LARGE}
            >
              {status === 'LOADING' ? '' : submitLabel}
              {status === 'LOADING' && <UitkLoader isSmall={true} isDarkBackground={true} />}
            </UitkPrimaryButton>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkText>
              <LocalizedText message="sign.up.have.an.account" />{' '}
              <UitkLink inline={true}>
                <Link to={`/${modifyLocaleParam(locale)}/signin`}>
                  <LocalizedText message="sign.up.login" />
                </Link>
              </UitkLink>
            </UitkText>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </form>
    </>
  );
};
